import { useEffect, useState } from "react";
import { useWorkspaces } from "../../controllers/useWorkspaces";
import { useUserDirectory } from "../../controllers/useUserDirectory";
import { UserCell, UserDirError, userOptionLabel } from "../components/UserCell";
import { useIssues } from "../../controllers/useIssues";
import { useBlog } from "../../controllers/useBlog";
import { useChat } from "../../controllers/useChat";
import type { ProjectRole } from "../../models/account";
import { IssuesView } from "./IssuesView";
import { BacklogView } from "./BacklogView";
import { BlogView } from "./BlogView";
import { ChatView } from "./ChatView";
import { CalendarView } from "./CalendarView";
import { GanttView } from "./GanttView";

const ROLE_LABEL: Record<ProjectRole, string> = {
  PROJECT_ADMIN: "프로젝트 관리자", EDITOR: "편집자", VIEWER: "뷰어",
};

type ProjectTab = "overview" | "issues" | "backlog" | "calendar" | "gantt" | "blog" | "chat";
const TAB_LABEL: Record<ProjectTab, string> = {
  overview: "개요", issues: "이슈", backlog: "백로그·스프린트", calendar: "캘린더", gantt: "간트", blog: "블로그", chat: "채팅",
};

// One project inside a workspace: overview (basic details + members), issues (board/list/detail),
// backlog+sprints+burndown, blog, and realtime chat. Each project-subsystem tab owns its own
// controller hook instance, scoped to this project's id.
export function ProjectDetailView({ ws, initialTab, currentUserId }: {
  ws: ReturnType<typeof useWorkspaces>;
  initialTab?: ProjectTab;
  /** The viewer's account id — the project chat socket needs it in the dev bypass. */
  currentUserId?: string | null;
}) {
  const project = ws.project!;
  const [tab, setTab] = useState<ProjectTab>(initialTab ?? "overview");

  const issues = useIssues(project.id);
  const blog = useBlog(project.id);
  const chat = useChat(project.id, "project", currentUserId);

  return (
    <div className="detail">
      <div className="detail-name">
        <h1>{project.name}</h1>
        <span className="mono">{project.slug}</span>
        <span className="badge ok">{project.status === "ACTIVE" ? "활성" : "보관됨"}</span>
      </div>
      <p className="meta">{project.description || "설명 없음"}</p>
      <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
        <button className="btn ghost" style={{ width: "auto" }} onClick={ws.backToWorkspace}>← 워크스페이스</button>
        {project.status === "ACTIVE" && (
          <button className="btn ghost" style={{ width: "auto" }} onClick={() => {
            if (window.confirm("이 프로젝트를 보관하시겠습니까?")) void ws.archiveCurrentProject();
          }}>보관</button>
        )}
      </div>

      {ws.error && <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}><b style={{ color: "var(--amber)" }}>오류:</b> {ws.error}</div>}

      <div className="tabs">
        {(Object.keys(TAB_LABEL) as ProjectTab[]).map((t) => (
          <button key={t} className={`tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>{TAB_LABEL[t]}</button>
        ))}
      </div>

      {tab === "overview" ? (
        ws.loadingProject ? (
          <div className="center">불러오는 중…</div>
        ) : (
          <Overview ws={ws} />
        )
      ) : tab === "issues" ? (
        <IssuesView issues={issues} members={ws.projectMembers} />
      ) : tab === "backlog" ? (
        <BacklogView issues={issues} />
      ) : tab === "calendar" ? (
        <CalendarView issues={issues} />
      ) : tab === "gantt" ? (
        <GanttView issues={issues} />
      ) : tab === "blog" ? (
        <BlogView blog={blog} />
      ) : (
        <ChatView chat={chat} members={ws.projectMembers} />
      )}
    </div>
  );
}

function Overview({ ws }: { ws: ReturnType<typeof useWorkspaces> }) {
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<ProjectRole>("VIEWER");
  const [busy, setBusy] = useState(false);

  const dir = useUserDirectory(ws.getToken);
  // Workspace members stay loaded while a project is open, so the "add member" picker can offer
  // people (이름·이메일) instead of asking for a raw user id.
  const candidates = ws.members.filter((wm) => !ws.projectMembers.some((pm) => pm.userId === wm.userId));
  useEffect(() => {
    dir.ensure([...ws.projectMembers.map((m) => m.userId), ...ws.members.map((m) => m.userId)]);
  }, [ws.projectMembers, ws.members, dir.ensure]);

  async function add() {
    setBusy(true);
    try {
      await ws.addMemberToProject(userId, role);
      setUserId("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="detail-body">
      <div className="detail-main">
        <h3>프로젝트 멤버 · {ws.projectMembers.length}</h3>
        <UserDirError dir={dir} />
        <table className="sdui-table">
          <thead><tr><th>멤버</th><th>역할</th><th data-align="end">작업</th></tr></thead>
          <tbody>
            {ws.projectMembers.map((m) => (
              <tr key={m.id}>
                <td><UserCell id={m.userId} dir={dir} /></td>
                <td>
                  <select
                    className="sdui-input" style={{ padding: "6px 8px" }}
                    value={m.role}
                    onChange={(e) => void ws.changeProjectMemberRole(m.userId, e.target.value as ProjectRole)}
                  >
                    {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
                <td data-align="end">
                  <button className="sdui-button sdui-button-secondary" onClick={() => {
                    if (window.confirm("이 멤버를 제거하시겠습니까?")) void ws.removeMemberFromProject(m.userId);
                  }}>제거</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {ws.projectMembers.length === 0 && <div className="sdui-table-empty">멤버가 없습니다.</div>}
      </div>

      <aside className="aside">
        <div className="info-card">
          <h4>멤버 추가</h4>
          <p style={{ fontSize: 13, color: "var(--ink3)", margin: "0 0 12px" }}>
            워크스페이스 멤버를 골라 프로젝트 역할을 부여합니다.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <label className="sdui-field">
              <span className="sdui-label">멤버</span>
              {ws.members.length > 0 ? (
                <select className="sdui-input" value={userId} onChange={(e) => setUserId(e.target.value)}>
                  <option value="">멤버 선택…</option>
                  {candidates.map((m) => (
                    <option key={m.userId} value={m.userId}>{userOptionLabel(m.userId, dir)}</option>
                  ))}
                </select>
              ) : (
                // Fallback so the flow is never dead if the workspace roster didn't load.
                <input className="sdui-input" placeholder="사용자 ID" value={userId} onChange={(e) => setUserId(e.target.value)} />
              )}
            </label>
            <label className="sdui-field">
              <span className="sdui-label">역할</span>
              <select className="sdui-input" value={role} onChange={(e) => setRole(e.target.value as ProjectRole)}>
                {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <button className="btn primary" disabled={!userId || busy} onClick={add}>
              {busy ? "추가 중…" : "추가"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
